import { JobApplication } from '../models/job-application.model';
import { StageId } from '../../company/models/stages.model';

/** Canonical pipeline order (matches backend status_order). */
export const PIPELINE_STATUS_ORDER = [
  'RECEIVED',
  'IN_PROGRESS',
  'ON_HOLD',
  'WAITING_FOR_FEEDBACK',
  'REJECTED',
  'ACCEPTED',
  'CLOSED',
] as const;

const HIDDEN_PIPELINE_STATUS_KEYS = new Set(['CLOSED', 'CUSTOM']);

const PIPELINE_STATUS_LABELS: Record<string, string> = {
  RECEIVED: 'Received',
  IN_PROGRESS: 'In Progress',
  ON_HOLD: 'On Hold',
  WAITING_FOR_FEEDBACK: 'Waiting for Feedback',
  REJECTED: 'Rejected',
  ACCEPTED: 'Accepted',
  CLOSED: 'Closed',
};

const UNKNOWN_STATUS_SORT_BASE = 1000;

export function formatStageLabel(name: string): string {
  if (!name.trim()) {
    return 'Unassigned';
  }
  const key = normalizeStageKey(name);
  return PIPELINE_STATUS_LABELS[key] ?? name.replace(/_/g, ' ').trim();
}

export function normalizeStageKey(name: string): string {
  return name.replace(/\s+/g, '_').trim().toUpperCase();
}

export function isVisiblePipelineStage(name: string): boolean {
  const key = normalizeStageKey(name);
  return Boolean(key) && !HIDDEN_PIPELINE_STATUS_KEYS.has(key);
}

export function pipelineStatusSortIndex(
  key: string,
  statusOrder?: number | null
): number {
  if (!key) {
    return -1;
  }

  if (statusOrder != null && statusOrder > 0) {
    return statusOrder;
  }

  const normalized = normalizeStageKey(key);
  const canonicalIndex = PIPELINE_STATUS_ORDER.indexOf(
    normalized as (typeof PIPELINE_STATUS_ORDER)[number]
  );
  if (canonicalIndex >= 0) {
    return canonicalIndex + 1;
  }

  return UNKNOWN_STATUS_SORT_BASE;
}

export function sortPipelineColumns(
  columns: PipelineColumn[]
): PipelineColumn[] {
  const unassigned = columns.filter((column) => column.id === 'unassigned');
  const rest = columns.filter((column) => column.id !== 'unassigned');

  rest.sort((left, right) => {
    const leftOrder = pipelineStatusSortIndex(left.key, left.statusOrder);
    const rightOrder = pipelineStatusSortIndex(right.key, right.statusOrder);
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.label.localeCompare(right.label);
  });

  return [...unassigned, ...rest];
}

export function statusesMatch(
  applicationStatus: string,
  stageName: string
): boolean {
  const left = normalizeStageKey(applicationStatus);
  const right = normalizeStageKey(stageName);
  return left.length > 0 && left === right;
}

export type PipelineColumnTone =
  | 'neutral'
  | 'progress'
  | 'waiting'
  | 'success'
  | 'danger'
  | 'closed';

const PIPELINE_TONE_BY_STATUS: Partial<Record<string, PipelineColumnTone>> = {
  RECEIVED: 'neutral',
  IN_PROGRESS: 'progress',
  ON_HOLD: 'waiting',
  WAITING_FOR_FEEDBACK: 'waiting',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  CLOSED: 'closed',
};

/** Maps ATS status to the premium theme's pipeline column tint (see design-tokens.css). */
export function getPipelineColumnTone(statusKey: string): PipelineColumnTone {
  if (!statusKey.trim()) {
    return 'neutral';
  }
  return PIPELINE_TONE_BY_STATUS[normalizeStageKey(statusKey)] ?? 'neutral';
}

export type PipelineColumn = {
  id: string;
  key: string;
  label: string;
  stageId?: string;
  statusOrder?: number;
  droppable: boolean;
};

export function buildPipelineColumns(
  stages: Pick<StageId, 'id' | 'name' | 'statusOrder'>[],
  applications: JobApplication[]
): PipelineColumn[] {
  const columns: PipelineColumn[] = [];

  for (const stage of stages) {
    if (!stage.name?.trim() || !isVisiblePipelineStage(stage.name)) {
      continue;
    }
    const key = normalizeStageKey(stage.name);
    columns.push({
      id: stage.id || `stage-${key}`,
      key,
      label: formatStageLabel(stage.name),
      stageId: stage.id || undefined,
      statusOrder: stage.statusOrder,
      droppable: Boolean(stage.id),
    });
  }

  const knownKeys = new Set(columns.map((column) => column.key));
  const extraStatusKeys = new Set<string>();

  applications.forEach((application) => {
    const key = normalizeStageKey(application.status || '');
    if (isVisiblePipelineStage(key) && !knownKeys.has(key)) {
      extraStatusKeys.add(key);
    }
  });

  extraStatusKeys.forEach((key) => {
    columns.push({
      id: `status-${key}`,
      key,
      label: formatStageLabel(key),
      droppable: false,
    });
  });

  const hasUnassigned = applications.some(
    (application) => !normalizeStageKey(application.status || '')
  );

  if (hasUnassigned) {
    columns.unshift({
      id: 'unassigned',
      key: '',
      label: 'Unassigned',
      droppable: false,
    });
  }

  if (columns.length === 0) {
    columns.push({
      id: 'unassigned',
      key: '',
      label: 'Unassigned',
      droppable: false,
    });
  }

  return sortPipelineColumns(columns);
}

export function buildColumnsFromApplicationStatuses(
  applications: JobApplication[]
): PipelineColumn[] {
  const keys = new Set<string>();

  applications.forEach((application) => {
    const key = normalizeStageKey(application.status || '');
    if (isVisiblePipelineStage(key)) {
      keys.add(key);
    }
  });

  const columns: PipelineColumn[] = Array.from(keys).map((key) => ({
    id: `status-${key}`,
    key,
    label: formatStageLabel(key),
    droppable: false,
  }));

  const hasUnassigned = applications.some(
    (application) => !normalizeStageKey(application.status || '')
  );

  if (hasUnassigned) {
    columns.unshift({
      id: 'unassigned',
      key: '',
      label: 'Unassigned',
      droppable: false,
    });
  }

  if (columns.length === 0) {
    columns.push({
      id: 'unassigned',
      key: '',
      label: 'Unassigned',
      droppable: false,
    });
  }

  return sortPipelineColumns(columns);
}

export function buildStandardPipelineColumns(): PipelineColumn[] {
  return PIPELINE_STATUS_ORDER.filter(isVisiblePipelineStage).map((key) => ({
    id: `status-${key}`,
    key,
    label: formatStageLabel(key),
    droppable: true,
  }));
}

export function groupApplicationsByColumn(
  applications: JobApplication[],
  columns: PipelineColumn[]
): Record<string, JobApplication[]> {
  const groups: Record<string, JobApplication[]> = {};
  columns.forEach((column) => {
    groups[column.id] = [];
  });

  applications.forEach((application) => {
    const statusKey = normalizeStageKey(application.status || '');
    const column =
      columns.find((item) => item.key === statusKey) ??
      columns.find((item) => item.id === 'unassigned');

    if (column) {
      groups[column.id].push(application);
    }
  });

  return groups;
}

export function findColumnForApplication(
  application: JobApplication,
  columns: PipelineColumn[]
): PipelineColumn | undefined {
  const statusKey = normalizeStageKey(application.status || '');
  return (
    columns.find((column) => column.key === statusKey) ??
    columns.find((column) => column.id === 'unassigned')
  );
}
